import { useEffect, useState, type ReactNode } from 'react';
import { BRAND, SLOUCHED, STROKE, UPRIGHT, blend, shape } from '@/brand/figure.js';
import { Icon } from '@/ui/icons.js';

/**
 * Landing intro: the app's figure at a desk straightening from slumped to
 * upright — the splash animation — and the live screen's "Good posture"
 * badge popping in when it lands. No joint dots: on the icon's heavy stroke
 * they read as rivets, and the carousel shows the real skeleton.
 */
const STRAIGHTEN_AT = 600;
const STRAIGHTEN_MS = 1300;

const ease = (t: number): number => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2);

function mix(a: string, b: string, t: number): string {
  const pa = [1, 3, 5].map((i) => parseInt(a.slice(i, i + 2), 16));
  const pb = [1, 3, 5].map((i) => parseInt(b.slice(i, i + 2), 16));
  return `rgb(${pa.map((v, i) => Math.round(v + (pb[i]! - v) * t)).join(',')})`;
}

function reducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function HeroFigure(): ReactNode {
  const [t, setT] = useState(() => (reducedMotion() ? 1 : 0));

  useEffect(() => {
    if (reducedMotion()) return;
    let frame = 0;
    const start = performance.now();
    const tick = (now: number): void => {
      const elapsed = now - start;
      setT(ease(Math.min(1, Math.max(0, (elapsed - STRAIGHTEN_AT) / STRAIGHTEN_MS))));
      if (elapsed < STRAIGHTEN_AT + STRAIGHTEN_MS) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, []);

  const pose = blend(SLOUCHED, UPRIGHT, t);
  const { d, head } = shape(pose);
  const color = mix(BRAND.slouched, BRAND.upright, t);
  const done = t >= 1;

  return (
    <div className="relative mx-auto w-full max-w-[280px] pt-12">
      <svg viewBox="110 64 350 390" className="block w-full" role="img" aria-label="A seated figure straightening up at a desk">
        {/* Desk and chair, drawn in the line colour so the figure carries the eye. */}
        <g stroke="var(--color-line)" strokeWidth="14" strokeLinecap="round" fill="none">
          <path d="M300 302 H440 M420 302 V440" />
          <path d="M176 372 H262 M190 372 V440 M176 372 V250" />
        </g>
        <g fill="none" stroke={color} strokeWidth={STROKE} strokeLinecap="round" strokeLinejoin="round">
          <path d={d} />
          <circle cx={head.cx} cy={head.cy} r={head.r} />
        </g>
      </svg>
      <div
        className="absolute left-1/2 top-0 flex -translate-x-1/2 items-center gap-1.5 whitespace-nowrap rounded-xl bg-state-good-soft px-3 py-1.5 text-sm font-bold text-state-good shadow-md"
        style={{
          opacity: done ? 1 : 0,
          transform: `translate(-50%, ${done ? 0 : 8}px) scale(${done ? 1 : 0.9})`,
          transition: 'opacity 300ms ease-out, transform 450ms cubic-bezier(.2,1.4,.4,1)',
        }}
        aria-hidden={!done}
      >
        <Icon name="check" size={16} strokeWidth={2.6} />
        Good posture
      </div>
    </div>
  );
}
